import { z } from 'zod';
import { take } from "rxjs/operators";
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import { DragulaService } from "ng2-dragula";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { DragContainerComponent, ModalControlComponent } from "@medsurf/flat-components";
import { ZodSharedValidator } from "@medsurf/flat-validators";

@Component({
    selector: 'medsurf-slide-edit-poi-annotation-form',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './poi-annotation-form.component.html',
    styleUrls: ['./poi-annotation-form.component.scss'],
    standalone: false
})
export class PoiAnnotationFormComponent extends DragContainerComponent {
  #isSubmitted = signal(false);
  poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null;
  poiAnnotationOptionArray: {value: string, name: any}[];
  poiAnnotationForm = new FormGroup(
    {
      id: new FormControl<null | string>(null)
    },
    {
      validators: ZodSharedValidator.validate(
        z.object({
          id: z.union([z.string().uuid().nullable(), z.literal('all')])
        })
      ),
      updateOn: 'change'
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public dialogService: DialogService,
              public translate: TranslateService,
              dragulaService: DragulaService) {
    super(dragulaService);

    
    // Update Poi
    this.slideAuthorFacade.currentSelectedSlidePoi$.pipe(takeUntilDestroyed()).subscribe((poi) => {
      this.poi = structuredClone(poi);
      if (this.poi) {
        this.slideAuthorFacade.poiAnnotationOptionArray$(this.poi, this.translate).pipe(take(1)).subscribe((poiAnnotationOptionArray) => {
          this.poiAnnotationOptionArray = poiAnnotationOptionArray;
        });
      } else {
        this.poiAnnotationOptionArray = [];
      }
    });
  }
  
  onSubmit() {
    if (this.#isSubmitted() || this.poiAnnotationForm.invalid || !this.poiAnnotationForm.value.id || !this.poi) {
      return;
    }
    this.#isSubmitted.set(true);
    
    if (this.poiAnnotationForm.value.id === 'all') {
      const annotations = this.poiAnnotationOptionArray.reduce((arr, option) => {
        if (option.value !== 'all') {
          arr.push(option.value);
        }
        return arr;
      }, [] as string[]);
      this.slideAuthorFacade.requestAddAnnotationIdsToPoi(this.poi, annotations, 'history.annotations_add_to_poi').subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.poiAnnotationForm);
          this.#isSubmitted.set(false);
          this.poiAnnotationForm.reset();
        },
        next: () => {
          this.#isSubmitted.set(false);
          this.poiAnnotationForm.reset();
        }
      });
    } else {
      // Request Add Annotation To Poi
      this.slideAuthorFacade.requestAddAnnotationIdToPoi(this.poi, this.poiAnnotationForm.value.id, 'history.annotation_add_to_poi').subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.poiAnnotationForm);
          this.#isSubmitted.set(false);
          this.poiAnnotationForm.reset();
        },
        next: () => {
          this.#isSubmitted.set(false);
          this.poiAnnotationForm.reset();
        }
      });
    }
  }
  
  //<editor-fold desc="Drag And Drop">
  
  onPoiAnnotationMoved(annotationToPointOfInterests: FlatModels.PointOfInterestEntityModels.AnnotationToPointOfInterest[]) {
    if (!this.poi) return;
    this.slideAuthorFacade.requestRearrangePoiAnnotations(this.poi, annotationToPointOfInterests).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  //</editor-fold>
  
  trackByAnnotationToPointOfInterest(index: number,
                                     item: FlatModels.PointOfInterestEntityModels.AnnotationToPointOfInterest) {
    return item.id || index;
  }
  
  goToAnnotation(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    this.slideAuthorFacade.requestGoToAnnotation(annotation).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  removeAnnotationFromPoi(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    if (!this.poi) return;
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.annotation_poi_remove'),
      data: {
        label: 'global.annotation_poi_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (this.poi && remove) {
        // Request Remove Annotation From Poi
        this.slideAuthorFacade.requestRemoveAnnotationFromPoi(this.poi, annotation.id, 'history.annotation_remove_from_poi').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }
}
